import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';

import {
  MATERIALS,
  sellUpComparison,
  validateRx,
  weightByMaterials,
  type RxCheck,
} from '../formulas';
import { formatMoney } from '@/lib/format';
import { formatFixed } from '../format';
import { NumField, Note, ResultBox, SelectField, useNumberField } from '../ui/primitives';
import styles from './cards.module.css';

// Блок 3 «Продавец-консультант» — калькуляторы 13–15.

export function WeightMaterialCalc(): JSX.Element {
  const { t } = useTranslation();
  const diameter = useNumberField('65');
  const power = useNumberField('-4');
  const ref = useNumberField('1.2');

  const rows = weightByMaterials({
    diameter: diameter.num,
    power: power.num,
    refThickness: ref.num,
  });

  return (
    <div>
      <NumField label={t('calculators.weight.diameter')} field={diameter} step="1" />
      <NumField label={t('calculators.weight.power')} field={power} step="0.25" />
      <NumField label={t('calculators.weight.refThickness')} field={ref} step="0.1" />
      <ResultBox>
        {rows.map((row) => (
          <div key={row.material.index} className={styles.materialRow}>
            <span>{row.material.name}</span>
            <span>
              {t('calculators.weight.row', {
                perLens: formatFixed(row.perLens, 1),
                perPair: formatFixed(row.perPair, 1),
              })}
            </span>
          </div>
        ))}
      </ResultBox>
      <Note>{t('calculators.weight.note')}</Note>
    </div>
  );
}

export function SellUpCalc(): JSX.Element {
  const { t, i18n } = useTranslation();
  const diameter = useNumberField('65');
  const power = useNumberField('-5');
  const ref = useNumberField('1.2');
  const priceA = useNumberField('15000');
  const priceB = useNumberField('38000');
  const [indexA, setIndexA] = useState('1.5');
  const [indexB, setIndexB] = useState('1.67');

  const result = sellUpComparison({
    diameter: diameter.num,
    power: power.num,
    refThickness: ref.num,
    indexA,
    priceA: priceA.num,
    indexB,
    priceB: priceB.num,
  });

  const materialOptions = MATERIALS.map((material) => ({
    value: String(material.index),
    label: material.name,
  }));

  // Прототип всегда писал «тоньше и легче» по модулю разницы; при обратном
  // сравнении (B дешевле и толще) это было неправдой — поэтому вариантов три.
  const gain = result.thicknessDiff;
  const resultKey = gain > 0 ? 'result' : gain < 0 ? 'resultReverse' : 'resultEqual';

  return (
    <div>
      <NumField label={t('calculators.sellUp.diameter')} field={diameter} step="1" />
      <NumField label={t('calculators.sellUp.power')} field={power} step="0.25" />
      <NumField label={t('calculators.sellUp.refThickness')} field={ref} step="0.1" />
      <SelectField
        label={t('calculators.sellUp.indexA')}
        value={indexA}
        onChange={setIndexA}
        options={materialOptions}
      />
      <NumField label={t('calculators.sellUp.priceA')} field={priceA} step="500" />
      <SelectField
        label={t('calculators.sellUp.indexB')}
        value={indexB}
        onChange={setIndexB}
        options={materialOptions}
      />
      <NumField label={t('calculators.sellUp.priceB')} field={priceB} step="500" />
      <ResultBox>
        {t(`calculators.sellUp.${resultKey}`, {
          thickness: formatFixed(Math.abs(result.thicknessDiff), 1),
          weight: formatFixed(Math.abs(result.weightDiffPair), 1),
          price: formatMoney(result.priceDiff, i18n.language),
        })}
      </ResultBox>
      <Note>{t('calculators.sellUp.note')}</Note>
    </div>
  );
}

/** Строка проверки RX: числа приходят из формул, текст — из словаря. */
function checkText(check: RxCheck, isMinus: boolean, t: TFunction): string {
  switch (check.id) {
    case 'diameter':
      return t('calculators.rx.checkDiameter', {
        required: formatFixed(check.limit, 1),
        selected: formatFixed(check.value, 1),
      });
    case 'decentration':
      return t('calculators.rx.checkDecentration', {
        value: formatFixed(check.value, 1),
        limit: check.limit,
      });
    case 'thickness':
      return t(
        isMinus ? 'calculators.rx.checkThicknessEdge' : 'calculators.rx.checkThicknessCenter',
        { value: formatFixed(check.value, 1), limit: check.limit },
      );
    case 'prism':
      return t('calculators.rx.checkPrism', {
        value: formatFixed(check.value),
        limit: check.limit,
      });
    case 'imbalance':
      return t('calculators.rx.checkImbalance', {
        value: formatFixed(check.value),
        limit: check.limit,
      });
  }
}

export function RxValidatorCalc(): JSX.Element {
  const { t } = useTranslation();
  const od = useNumberField('-5');
  const os = useNumberField('-1.5');
  const cyl = useNumberField('-1');
  const a = useNumberField('54');
  const dbl = useNumberField('18');
  const pd = useNumberField('62');
  const diameter = useNumberField('65');
  const margin = useNumberField('2');
  const index = useNumberField('1.6');

  const result = validateRx({
    odPower: od.num,
    osPower: os.num,
    cyl: cyl.num,
    a: a.num,
    dbl: dbl.num,
    pd: pd.num,
    diameter: diameter.num,
    margin: margin.num,
    index: index.num,
  });

  return (
    <div>
      <NumField label={t('calculators.rx.od')} field={od} step="0.25" />
      <NumField label={t('calculators.rx.os')} field={os} step="0.25" />
      <NumField label={t('calculators.rx.cyl')} field={cyl} step="0.25" />
      <NumField label={t('calculators.rx.a')} field={a} step="1" />
      <NumField label={t('calculators.rx.dbl')} field={dbl} step="1" />
      <NumField label={t('calculators.rx.pd')} field={pd} step="1" />
      <NumField label={t('calculators.rx.diameter')} field={diameter} step="1" />
      <NumField label={t('calculators.rx.margin')} field={margin} step="0.5" />
      <NumField label={t('calculators.rx.index')} field={index} step="0.01" />

      <div className={styles.checks}>
        {result.checks.map((check) => (
          <div key={check.id} className={styles.check}>
            {check.ok ? (
              <CheckCircle2
                size={14}
                aria-hidden="true"
                className={styles.checkIcon}
                color="var(--sv-teal)"
              />
            ) : (
              <AlertTriangle
                size={14}
                aria-hidden="true"
                className={styles.checkIcon}
                color="var(--sv-warn)"
              />
            )}
            <span>{checkText(check, result.isMinus, t)}</span>
          </div>
        ))}
      </div>
      <ResultBox>
        {t(result.allOk ? 'calculators.rx.resultOk' : 'calculators.rx.resultRisk')}
      </ResultBox>
      <Note>{t('calculators.rx.note')}</Note>
    </div>
  );
}
